<template>
	<div class="flex w-full justify-center">
		<div class="pb-20 lg:pt-10 w-full max-w-5xl lg:max-w-6xl xl:max-w-7xl">
			<CartItems ref="cartComponent" v-slot="{ emptyBag, reduceItemQty, increaseItemQty, removeItem, reduceCertQty, increaseCertQty, removeCert }">
				<div class="flex justify-center md:pb-6 md:pt-0 fixed md:static top-20 md:top-0 z-20 bg-white w-full">
					<div class="flex w-full m-auto justify-between items-center border-t md:border-none shadow md:shadow-none py-6 px-4 md:px-8">
						<div class="flex w-full m-auto gap-x-2" :class="!store.cartTotalItems ? 'justify-center items-center' : 'text-2xl'">
							<div class="rounded-full" v-if="store.cartTotalItems">
								<FooterLogo width="65px" />
							</div>
							<div class="flex flex-col">
								<div class="text-ihh-midnight-gray font-semibold flex mr-2" :class="!store.cartTotalItems ? 'text-4xl' : 'text-2xl'">
									<span class="mr-1.5">Your Cart</span>
									<span v-if="!store.cartTotalItems">is Empty</span>
								</div>
								<div v-if="store.cartTotalItems" class="text-sm text-ihh-gray-500 font-normal">
									<span class="pl-1">{{ (store?.cart?.cartItems?.length || 0) + (store?.cart?.certificates?.length || 0) }}</span>
									<span>{{ store.cartTotalItems === 1 ? ' Item' : ' Items' }}</span>
								</div>
							</div>
						</div>
						<div v-if="store.cartTotalItems" class="">
							<div class="flex flex-col">
								<span class="font-semibold text-xl text-ihh-midnight-gray">{{
									(store.cart.subTotal || store.cartSubtotal).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
								}}</span>
								<span class="text-sm text-ihh-gray-500">Subtotal</span>
							</div>
						</div>
					</div>
				</div>

				<div class="px-4 md:px-0 pt-16 md:pt-0">
					<div :class="store.cartTotalItems === 0 ? 'flex w-full justify-center cursor-pointer group pt-32 md:pt-8' : 'hidden'">
						<router-link class="hover:bg-ihh-salmon bg-ihh-dark-salmon px-4 py-2 rounded text-white text-base" :to="`/`"> Continue Shopping </router-link>
					</div>

					<div :class="store.cart.tax === null ? 'flex w-full justify-center items-center' : 'hidden'">
						<div :class="store.cartTotalItems >= 1 ? 'flex w-full justify-center items-center inset-0 fixed bg-ihh-white/75 z-50' : 'hidden'">
							<Spinner :small="true" color="border-t-ihh-salmon" class="delay-500 w-28 h-28" />
						</div>
					</div>

					<!-- Cart Items Table -->
					<div class="hidden md:flex px-8">
						<table :class="!store.cartTotalItems ? 'hidden' : 'table-auto w-full justify-between'">
							<thead class="">
								<tr class="border-b text-ihh-slate-gray">
									<th class="p-4 text-start">Item</th>
									<th class="p-4 text-center">Price</th>
									<th class="p-4 text-center">Quantity</th>
									<th class="p-4 text-center">Total</th>
								</tr>
							</thead>

							<tbody class="table-auto w-full justify-between">
								<CartItemDesktop
									v-for="{ item, quantity, variant, group, optional, optionSelections, price } in store.cart.cartItems"
									:key="item.id"
									:item="{ item, quantity, variant, group, optional, optionSelections, price }"
									:XCoverProducts="XCoverProducts"
									@reduce-qty="reduceItemQty"
									@increase-qty="increaseItemQty"
									@remove-item="removeItem"
								/>
							</tbody>
						</table>
					</div>

					<!-- Show this if screen is under md: size -->
					<div class="md:hidden mt-12">
						<CartItemMobile
							v-for="{ item, quantity, variant, group, optional, optionSelections, price } in store.cart.cartItems"
							:key="item.id"
							:item="{ item, quantity, variant, group, optional, optionSelections, price }"
							:XCoverProducts="XCoverProducts"
							@reduce-qty="reduceItemQty"
							@increase-qty="increaseItemQty"
							@remove-item="removeItem"
						/>
					</div>

					<!-- Gift Certificates Table -->
					<div class="hidden md:flex px-8">
						<table class="table-auto w-full justify-between">
							<tbody class="table-auto w-full justify-between">
								<CartCertificateDesktop
									v-for="(cert, index) in store.cart.certificates"
									:key="index"
									:certificate="cert"
									:index="index"
									@reduce-qty="reduceCertQty"
									@increase-qty="increaseCertQty"
									@remove-cert="removeCert"
								/>
							</tbody>
						</table>
					</div>

					<!-- Mobile Gift Certificates -->
					<div class="md:hidden mt-8 border-t">
						<CartCertificateMobile
							v-for="(cert, index) in store.cart.certificates"
							:key="index"
							:certificate="cert"
							:index="index"
							@reduce-qty="reduceCertQty"
							@increase-qty="increaseCertQty"
							@remove-cert="removeCert"
						/>
					</div>

					<div class="w-full px-10 max-h-[420px]">
						<RelatedProducts class="print:hidden" :product="currentItem" />
					</div>

					<div
						class="flex flex-col md:flex-row w-full items-center justify-center md:items-start pt-20 px-4 sm:px-8"
						:class="!store.cartTotalItems ? 'flex w-full justify-center' : 'gap-y-8 md:gap-x-8 md:justify-between'"
					>
						<div id="cart-summary" v-if="store.cartTotalItems" class="flex flex-col w-full md:max-w-xl text-ihh-gray-700 md:order-2 space-y-4">
							<!-- Subtotal -->
							<div class="flex justify-between mb-1 w-full border-b pb-4">
								<span class="font-bold">Subtotal:</span>
								<span>{{ (store.cart.subTotal || store.cartSubtotal).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}</span>
							</div>

							<!-- Promotions -->
							<div v-if="hasPromotions" class="flex justify-between mb-1 w-full border-b pb-4">
								<span class="text-[#F44631] font-ihh-bold">Discount</span>
								<span>-{{ promotionsTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}</span>
							</div>

							<!-- Shipping -->
							<div class="w-full border-b pb-4">
								<div class="flex m-auto items-center justify-between mb-1">
									<div class="flex-grow">
										<div class="font-bold">Shipping:</div>
									</div>
									<div>
										<button
											@click="showShippingInfo = !showShippingInfo"
											class="underline cursor-pointer w-16 text-right font-normal"
											:class="checkout.shipping.selectedShipping?.id ? 'hover:text-ihh-salmon text-ihh-dark-salmon' : ''"
										>
											{{ checkout.shippingText }}
										</button>
									</div>
								</div>
								<div>
									<Shipping v-if="showShippingInfo" @hideShipping="showShippingInfo = false" class="text-ihh-midnight-gray" />
								</div>
							</div>

							<!-- Coupon Code -->
							<div class="w-full border-b pb-4">
								<Coupon :style="{ title: 'font-bold text-ihh-gray-700', remove: 'text-sm' }" />
							</div>

							<!-- Gift Certificate -->
							<div class="w-full border-b pb-4">
								<Certificate :style="{ title: 'font-bold text-ihh-gray-700', remove: 'text-sm' }" />
							</div>

							<!-- Total -->
							<div class="flex justify-between text-2xl text-ihh-midnight-gray font-bold mt-10">
								<span>Total</span>
								<Spinner v-if="store.cart.tax === null" :small="true" class="w-4 h-4" color="border-t-ihh-salmon" />
								<span v-else>{{ checkout.displayTotal?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}</span>
							</div>
						</div>

						<!-- Checkout Buttons -->
						<div class="flex flex-col space-y-4 w-full md:max-w-sm md:order-1">
							<button
								:class="[
									{ 'opacity-40 pointer-events-none': emptyCart || store.cart?.tax === null },
									'rounded flex text-center items-center justify-center text-sm md:text-base text-ihh-midnight-gray py-3 px-10 w-full bg-ihh-dark-salmon hover:bg-ihh-salmon duration-150 tracking-wide font-semibold transition-transform active:scale-95 shadow-md hover:shadow-lg',
								]"
							>
								<router-link to="/checkout" class="w-full h-full flex items-center justify-center text-[16px] text-ihh-midnight-gray no-underline">
									Proceed to Checkout
									<div v-if="!emptyCart" class="ml-1 flex">
										<Spinner v-if="store.cart.tax === null" :small="true" class="w-4 h-4" color="border-t-ihh-salmon" />
										<span v-else>({{ checkout.displayTotal?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }})</span>
									</div>
								</router-link>
							</button>
							<button
								@click="emptyBag"
								class="rounded flex text-[16px] w-full text-center justify-center text-ihh-midnight-gray border-3 border-ihh-salmon px-8 py-3 hover:bg-ihh-salmon duration-150 whitespace-nowrap font-semibold"
								:class="[{ 'opacity-40 pointer-events-none': !store.cart.cartItems.length }]"
							>
								Remove all items
							</button>
						</div>
					</div>
				</div>
			</CartItems>
		</div>
	</div>
</template>

<script>
import CartItems from '../../layout/cart-items.vue'
import CartItemDesktop from '../../layout/cart-item-desktop.vue'
import CartItemMobile from '../../layout/cart-item-mobile.vue'
import CartCertificateDesktop from '../../layout/cart-certificate-desktop.vue'
import CartCertificateMobile from '../../layout/cart-certificate-mobile.vue'
import Spinner from '../../layout/spinner.vue'
import Shipping from './shipping.vue'
import LogoSmall from '../../partials/logo-small.vue'
import PayPalButton from '../../checkout/paypal-button.vue'
import VenmoButton from '../../checkout/venmo-button.vue'
import ApplePayButton from '../../checkout/apple-pay-button.vue'
import FooterLogo from '../../partials/footer-logo.vue'
import { useCheckoutStore } from '../../../stores/store.js'
import { validateInventory } from '../../../helpers/validateCart.js'
import Coupon from '../../checkout/coupon.vue'
import Certificate from '../../checkout/certificate.vue'
import RelatedProducts from '../ihh-items/related-products.vue'

export default {
	components: {
		CartItems,
		CartItemDesktop,
		CartItemMobile,
		CartCertificateDesktop,
		CartCertificateMobile,
		Shipping,
		Spinner,
		Coupon,
		Certificate,
		VenmoButton,
		LogoSmall,
		PayPalButton,
		ApplePayButton,
		FooterLogo,
		RelatedProducts,
	},
	inject: ['store'],
	data() {
		return {
			checkout: useCheckoutStore(),
			showApplePay: true,
			showVenmo: true,
			showShippingInfo: false,
			XCoverProducts: [3693, 3694, 3900, 3901, 4335, 4336],
		}
	},

	mounted() {
		validateInventory()
	},

	methods: {
		toggleBag() {
			console.log('toggleBag clicked')
			this.store.showBag = !this.store.showBag
		},
		handleAppleAvailability(value) {
			this.showApplePay = value
		},
		handleVenmoAvailability(value) {
			this.showVenmo = value
		},
	},
	computed: {
		emptyCart() {
			return this.store.cart?.cartItems?.length === 0 && this.store.cart?.certificates?.length === 0
		},
		currentItem() {
			return this.store.cart?.cartItems?.[this.store.cart?.cartItems?.length - 1]?.item
		},
		hasPromotions() {
			// Only show promotions if the discount is at least $0.01
			// This prevents showing $0 promotions due to rounding or timing issues
			return this.promotionsTotal >= 0.01
		},
		promotionsTotal() {
			const base = this.store.cart?.baseAmount || 0
			const exTax = this.store.cart?.cartAmountExTax || 0
			const coupon = this.store?.cart?.couponDiscount || 0
			const discount = base - exTax - coupon
			// Round to 2 decimal places to avoid floating-point precision issues
			return Math.round(discount * 100) / 100
		},
	},
}
</script>

<style scoped>
input {
	@apply outline-none focus:ring-ihh-midnight-gray focus:border-ihh-midnight-gray;
}
</style>
